/**
 * DetailsTab — payment terms, status, billing details, payments & reconciliation,
 * recurring schedule, and shipping & delivery fields.
 *
 * Extracted from DocumentModal.tsx to reduce render complexity.
 * Purely presentational: all state and handlers are passed as props.
 */

import React from 'react';
import { User, Clock, CreditCard, RefreshCw, Loader2, Route, Gauge, MapPin, FileText, Building2, Calendar, Fuel, Truck } from 'lucide-react';
import { type DocumentConfig, type DocumentFormState } from '../documentModalHelpers';
import { type Document, type DocumentType, type DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import { FinanceStatusBadge } from '../DocumentModalShared';
import { formatDateSAST, formatDateTimeSAST } from '@/lib/sastTime';
import { type UserAction as LifecycleAction } from '@/lib/finance/documentLifecycle';

/** Currency config shape from useDocumentTotals hook. */
type CurrencyConfig = Record<string, { symbol: string; name: string; locale: string; flag: string }>;

/** Payment row shape — matches the inline interface in DocumentModal. */
export interface DocumentPaymentRow {
  allocation_id: string;
  allocated_amount: number;
  payment_id: string | null;
  payment_number: string | null;
  payment_date: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  payment_type: string | null;
  payment_amount: number | null;
  payment_status: string | null;
  is_reconciled: boolean | null;
  allocation_strategy: string | null;
  bank_transaction_id: string | null;
  bank_transaction_date: string | null;
  bank_description: string | null;
  bank_reference: string | null;
  bank_amount: number | null;
  bank_type: string | null;
  bank_account_name: string | null;
  bank_name: string | null;
}

/** Recurring settings shape — matches the interface in useDocumentLifecycle. */
export interface RecurringSettings {
  frequency: 'daily' | 'weekly' | 'bi-weekly' | 'monthly' | 'quarterly' | 'yearly';
  start_date: string;
  end_date: string;
  day_of_month: number;
  day_of_week: number;
  auto_send: boolean;
  auto_approve: boolean;
  generate_days_before: number;
}

export interface DetailsTabProps {
  isActive: boolean;
  isDarkMode: boolean;
  isCompactMobileDoc: boolean;
  formData: DocumentFormState;
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormState>>;
  config: DocumentConfig;
  doc_type: DocumentType;
  document: Document | null | undefined;
  documentId: string | number | undefined;
  items: DocumentLineItem[];
  isDocumentStatusSystemManaged: boolean;
  // Contact info (for billing submission display)
  selectedContactName: string;
  selectedContactPersonSummary: string;
  selectedLocationSummary: string;
  // Lifecycle actions
  lifecycleActions: LifecycleAction[];
  performLifecycleAction: (action: LifecycleAction) => Promise<void>;
  performingActionId: string | null;
  // Currency
  CURRENCY_CONFIG: CurrencyConfig;
  // Date formatting
  formatDateSAST: typeof formatDateSAST;
  formatDateTimeSAST: typeof formatDateTimeSAST;
  // Billing submission
  handleRegenerateBilling: () => Promise<void>;
  regeneratingBilling: boolean;
  // Payments & reconciliation
  loadDocumentPayments: (documentId: string | number) => Promise<void>;
  loadingPayments: boolean;
  paymentsError: string | null;
  paymentsTotals: { grand_total: number; amount_paid: number; balance_due: number; total_allocated: number } | null;
  documentPayments: DocumentPaymentRow[];
  handleUnmatchPayment: (row: DocumentPaymentRow) => Promise<void>;
  // Recurring
  createAsRecurring: boolean;
  setCreateAsRecurring: React.Dispatch<React.SetStateAction<boolean>>;
  recurringSettings: RecurringSettings;
  setRecurringSettings: React.Dispatch<React.SetStateAction<RecurringSettings>>;
}

export function DetailsTab({
  isActive,
  isDarkMode,
  isCompactMobileDoc,
  formData,
  setFormData,
  config,
  doc_type,
  document,
  documentId,
  items,
  isDocumentStatusSystemManaged,
  selectedContactName,
  selectedContactPersonSummary,
  selectedLocationSummary,
  lifecycleActions,
  performLifecycleAction,
  performingActionId,
  CURRENCY_CONFIG,
  formatDateSAST: fmtDateSAST,
  formatDateTimeSAST: fmtDateTimeSAST,
  handleRegenerateBilling,
  regeneratingBilling,
  loadDocumentPayments,
  loadingPayments,
  paymentsError,
  paymentsTotals,
  documentPayments,
  handleUnmatchPayment,
  createAsRecurring,
  setCreateAsRecurring,
  recurringSettings,
  setRecurringSettings,
}: DetailsTabProps) {
  if (!isActive) return null;

  // ── Travel/mileage-specific fields ──
  // Shown when the expense is a travel subtype (entry_type === 'travel' in custom_fields).
  const cf = (formData.custom_fields as Record<string, unknown> | undefined) || {};
  const docCf = (document?.custom_fields as Record<string, unknown> | undefined) || {};
  const isTravelExpense = cf.entry_type === 'travel' || docCf.entry_type === 'travel';
  const travelDistanceKm: number | string | null = (cf.distance_km as number | string | undefined) ?? (docCf.distance_km as number | string | undefined) ?? null;
  const travelRatePerKm: number | string | null = (cf.rate_per_km as number | string | undefined) ?? (docCf.rate_per_km as number | string | undefined) ?? null;
  const travelVehicleReg: string | null = (cf.vehicle_registration as string | undefined) ?? (docCf.vehicle_registration as string | undefined) ?? null;
  const travelTripIds: string | null = (cf.trip_ids as string | undefined) ?? (docCf.trip_ids as string | undefined) ?? null;
  const travelTripCount: number | string | null = (cf.trip_count as number | string | undefined) ?? (docCf.trip_count as number | string | undefined) ?? null;

  // ── Fuel-specific fields ──
  // Shown when the expense is a fuel subtype (entry_type === 'fuel' in custom_fields).
  const isFuelExpense = cf.entry_type === 'fuel' || docCf.entry_type === 'fuel';
  const creatorName = document?.creator
    ? [document.creator.first_name, document.creator.last_name].filter(Boolean).join(' ').trim()
    : '';
  const fuelWorkerName: string | null = (cf.worker_name as string | undefined)
    ?? (docCf.worker_name as string | undefined)
    ?? (creatorName || null);
  const fuelVehicleReg: string | null = (cf.vehicle_registration as string | undefined) ?? (docCf.vehicle_registration as string | undefined) ?? null;
  const fuelOdometer: number | string | null = (cf.odometer as number | string | undefined) ?? (docCf.odometer as number | string | undefined) ?? null;
  const fuelLitres: number | string | null = (cf.litres as number | string | undefined) ?? (docCf.litres as number | string | undefined) ?? null;
  const fuelType: string | null = (cf.fuel_type as string | undefined) ?? (docCf.fuel_type as string | undefined) ?? null;
  const fuelStation: string | null = (cf.station as string | undefined) ?? (docCf.station as string | undefined) ?? null;

  // ── Per diem-specific fields ──
  // Shown when the expense is a per diem claim (per_diem_auto_generated === true in custom_fields).
  const isPerDiem = cf.per_diem_auto_generated === true || docCf.per_diem_auto_generated === true;
  const perDiemDays: number | string | null = (cf.per_diem_days as number | string | undefined) ?? (docCf.per_diem_days as number | string | undefined) ?? null;
  const perDiemFullDays: number | string | null = (cf.per_diem_full_days as number | string | undefined) ?? (docCf.per_diem_full_days as number | string | undefined) ?? null;
  const perDiemPartialDays: number | string | null = (cf.per_diem_partial_days as number | string | undefined) ?? (docCf.per_diem_partial_days as number | string | undefined) ?? null;
  const perDiemDailyRate: number | string | null = (cf.per_diem_daily_rate as number | string | undefined) ?? (docCf.per_diem_daily_rate as number | string | undefined) ?? null;
  const perDiemPartialDayRate: number | string | null = (cf.per_diem_partial_day_rate as number | string | undefined) ?? (docCf.per_diem_partial_day_rate as number | string | undefined) ?? null;
  const perDiemDestinationName: string | null = (cf.per_diem_destination_name as string | undefined) ?? (docCf.per_diem_destination_name as string | undefined) ?? null;
  const perDiemDestinationCity: string | null = (cf.per_diem_destination_city as string | undefined) ?? (docCf.per_diem_destination_city as string | undefined) ?? null;
  const perDiemContactName: string | null = (cf.per_diem_contact_name as string | undefined) ?? (docCf.per_diem_contact_name as string | undefined) ?? null;
  const perDiemEmployeeName: string | null = (cf.per_diem_employee_name as string | undefined) ?? (docCf.per_diem_employee_name as string | undefined) ?? null;
  const perDiemStartDate: string | null = (cf.per_diem_start_date as string | undefined) ?? (docCf.per_diem_start_date as string | undefined) ?? null;
  const perDiemEndDate: string | null = (cf.per_diem_end_date as string | undefined) ?? (docCf.per_diem_end_date as string | undefined) ?? null;

  return (
    <div className={`grid gap-6 ${isCompactMobileDoc ? 'grid-cols-1' : 'grid-cols-2'}`}>
      <div className="space-y-4">
        {/* Travel / Mileage Details */}
        {isTravelExpense && (
          <div className={`rounded-lg border p-4 ${isDarkMode ? 'bg-indigo-950/20 border-indigo-800' : 'bg-indigo-50 border-indigo-200'}`}>
            <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
              <Route className="w-4 h-4" />
              Travel / Mileage Details
            </h4>
            <div className="space-y-2 text-sm">
              {travelDistanceKm != null && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Gauge className="w-3.5 h-3.5" /> Distance
                  </span>
                  <span className="font-medium">{Number(travelDistanceKm).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km</span>
                </div>
              )}
              {travelRatePerKm != null && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <CreditCard className="w-3.5 h-3.5" /> Rate / km
                  </span>
                  <span className="font-medium">R {Number(travelRatePerKm).toFixed(2)}</span>
                </div>
              )}
              {travelVehicleReg && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <MapPin className="w-3.5 h-3.5" /> Vehicle
                  </span>
                  <span className="font-mono font-medium">{travelVehicleReg}</span>
                </div>
              )}
              {travelTripCount != null && Number(travelTripCount) > 0 && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <FileText className="w-3.5 h-3.5" /> Trips
                  </span>
                  <span className="font-medium">{travelTripCount} trip{Number(travelTripCount) > 1 ? 's' : ''} submitted</span>
                </div>
              )}
              {travelTripIds && (
                <div className="pt-2 mt-2 border-t border-indigo-200 dark:border-indigo-800">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Linked logbook trip IDs:</span>
                  <p className={`text-xs font-mono mt-1 break-all ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{travelTripIds}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Fuel Details */}
        {isFuelExpense && (
          <div className={`rounded-lg border p-4 ${isDarkMode ? 'bg-amber-950/20 border-amber-800' : 'bg-amber-50 border-amber-200'}`}>
            <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
              <Fuel className="w-4 h-4" />
              Fuel Details
            </h4>
            <div className="space-y-2 text-sm">
              {fuelWorkerName && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <User className="w-3.5 h-3.5" /> Driver
                  </span>
                  <span className="font-medium">{fuelWorkerName}</span>
                </div>
              )}
              {fuelVehicleReg && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Truck className="w-3.5 h-3.5" /> Vehicle
                  </span>
                  <span className="font-mono font-medium">{fuelVehicleReg}</span>
                </div>
              )}
              {fuelOdometer != null && Number(fuelOdometer) > 0 && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Gauge className="w-3.5 h-3.5" /> Odometer
                  </span>
                  <span className="font-medium">{Number(fuelOdometer).toLocaleString()} km</span>
                </div>
              )}
              {fuelLitres != null && Number(fuelLitres) > 0 && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Fuel className="w-3.5 h-3.5" /> Volume
                  </span>
                  <span className="font-medium">{Number(fuelLitres).toFixed(2)} L</span>
                </div>
              )}
              {fuelType && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Fuel className="w-3.5 h-3.5" /> Fuel Type
                  </span>
                  <span className="font-medium">{fuelType}</span>
                </div>
              )}
              {fuelStation && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <MapPin className="w-3.5 h-3.5" /> Station
                  </span>
                  <span className="font-medium">{fuelStation}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Per Diem Details */}
        {isPerDiem && (
          <div className={`rounded-lg border p-4 ${isDarkMode ? 'bg-emerald-950/20 border-emerald-800' : 'bg-emerald-50 border-emerald-200'}`}>
            <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
              <Clock className="w-4 h-4" />
              Per Diem Details
            </h4>
            <div className="space-y-2 text-sm">
              {perDiemEmployeeName && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <User className="w-3.5 h-3.5" /> Employee
                  </span>
                  <span className="font-medium">{perDiemEmployeeName}</span>
                </div>
              )}
              {perDiemContactName && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Building2 className="w-3.5 h-3.5" /> Destination Contact
                  </span>
                  <span className="font-medium">{perDiemContactName}</span>
                </div>
              )}
              {perDiemDestinationName && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <MapPin className="w-3.5 h-3.5" /> Destination
                  </span>
                  <span className="font-medium">{perDiemDestinationName}{perDiemDestinationCity ? `, ${perDiemDestinationCity}` : ''}</span>
                </div>
              )}
              {perDiemStartDate && perDiemEndDate && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Calendar className="w-3.5 h-3.5" /> Travel Period
                  </span>
                  <span className="font-medium">{perDiemStartDate} → {perDiemEndDate}</span>
                </div>
              )}
              {perDiemDailyRate != null && (
                <div className="flex justify-between">
                  <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <CreditCard className="w-3.5 h-3.5" /> Daily Rate
                  </span>
                  <span className="font-medium">R {Number(perDiemDailyRate).toFixed(2)}</span>
                </div>
              )}
              {perDiemFullDays != null && Number(perDiemFullDays) > 0 && (
                <div className="flex justify-between">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Full Days</span>
                  <span className="font-medium">{perDiemFullDays}</span>
                </div>
              )}
              {perDiemPartialDays != null && Number(perDiemPartialDays) > 0 && (
                <div className="flex justify-between">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Partial Days</span>
                  <span className="font-medium">{perDiemPartialDays} @ R {Number(perDiemPartialDayRate || 0).toFixed(2)}</span>
                </div>
              )}
              {perDiemDays != null && (
                <div className="flex justify-between border-t border-emerald-200 dark:border-emerald-800 pt-2 mt-2">
                  <span className="font-medium">Total Days</span>
                  <span className="font-bold">{perDiemDays}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <h3 className={`font-semibold border-b pb-2 ${isDarkMode ? 'text-white border-gray-700' : 'text-gray-900 border-gray-200'
          }`}>Payment & Terms</h3>

        {/* Currency Selector */}
        <div>
          <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
            Currency
          </label>
          <div className="flex gap-2">
            <select
              value={formData.currency || 'ZAR'}
              onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
              className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
                }`}
            >
              {Object.entries(CURRENCY_CONFIG).map(([code, cfg]) => (
                <option key={code} value={code}>
                  {cfg.flag} {code} - {cfg.name}
                </option>
              ))}
            </select>
            {formData.currency && formData.currency !== 'ZAR' && (
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  &nbsp;
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={formData.exchange_rate || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, exchange_rate: parseFloat(e.target.value) || undefined }))}
                    placeholder="Exchange rate"
                    className={`w-36 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    → ZAR
                  </span>
                </div>
              </div>
            )}
          </div>
          {formData.currency && formData.currency !== 'ZAR' && formData.exchange_rate && (
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              1 {formData.currency} = {formData.exchange_rate} ZAR
              {formData.grand_total ? ` (≈ R ${(formData.grand_total * formData.exchange_rate).toFixed(2)} ZAR)` : ''}
            </p>
          )}
        </div>

        {config.fields.showPaymentTerms && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              Payment Terms (days)
            </label>
            <input
              type="number"
              value={formData.payment_terms || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: Number(e.target.value) }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
                }`}
              placeholder="e.g., 30"
            />
          </div>
        )}

        {config.fields.showStatus && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              Status
            </label>

            {/* Read-only status badge — the lifecycle is system-managed.
          Paid / Partial / Overdue / Expired are driven by bank
          reconciliation and dates, not by user input. */}
            <div className="flex items-center gap-2 flex-wrap">
              <FinanceStatusBadge
                status={String(formData.status || 'draft')}
                isDark={isDarkMode}
                itemType={doc_type}
              />
              {formData.payment_status && formData.payment_status !== formData.status && (
                <FinanceStatusBadge
                  status={String(formData.payment_status)}
                  isDark={isDarkMode}
                  itemType={doc_type}
                />
              )}
            </div>

            {/* User-driven transitions available from this status. */}
            {documentId && lifecycleActions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {lifecycleActions.map((action) => {
                  const toneClass =
                    action.tone === 'danger'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : action.tone === 'success'
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : action.tone === 'warning'
                          ? 'bg-amber-500 hover:bg-amber-600 text-white'
                          : action.tone === 'primary'
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : isDarkMode
                              ? 'bg-gray-700 hover:bg-gray-600 text-gray-100 border border-gray-600'
                              : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300';
                  const isBusy = performingActionId === action.id;
                  return (
                    <button
                      key={action.id}
                      type="button"
                      onClick={() => performLifecycleAction(action)}
                      disabled={performingActionId !== null}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${toneClass}`}
                      title={action.confirm || action.label}
                    >
                      {isBusy ? 'Working…' : action.label}
                    </button>
                  );
                })}
              </div>
            )}

            {!isDocumentStatusSystemManaged && doc_type !== 'billing_submission' && (
              <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Status transitions are driven by the actions above, and by bank reconciliation once a payment is matched.
              </p>
            )}
          </div>
        )}

        {/* Billing Submission specific fields */}
        {doc_type === 'billing_submission' && (
          <div className="mt-4 space-y-4">
            <h3 className={`font-semibold border-b pb-2 ${isDarkMode ? 'text-white border-gray-700' : 'text-gray-900 border-gray-200'
              }`}>Billing Submission Details</h3>

            {/* Contact Information */}
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                Contact
              </label>
              <div className={`px-3 py-2 rounded-lg border ${isDarkMode
                ? 'bg-gray-800 border-gray-700 text-gray-200'
                : 'bg-gray-50 border-gray-200 text-gray-700'
                }`}>
                <div className="font-medium">{selectedContactName}</div>
                {selectedContactPersonSummary && selectedContactPersonSummary !== 'No person selected' && (
                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Person: {selectedContactPersonSummary}
                  </div>
                )}
                {selectedLocationSummary && selectedLocationSummary !== 'No location selected' && (
                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Location: {selectedLocationSummary}
                  </div>
                )}
              </div>
            </div>

            {/* Created By */}
            {document?.created_by && (
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Created By
                </label>
                <div className={`px-3 py-2 rounded-lg border ${isDarkMode
                  ? 'bg-gray-800 border-gray-700 text-gray-200'
                  : 'bg-gray-50 border-gray-200 text-gray-700'
                  }`}>
                  <div className="flex items-center gap-2">
                    <User size={14} />
                    <span className="text-sm">User ID: {document.created_by}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Created At */}
            {document?.created_at && (
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Created At
                </label>
                <div className={`px-3 py-2 rounded-lg border ${isDarkMode
                  ? 'bg-gray-800 border-gray-700 text-gray-200'
                  : 'bg-gray-50 border-gray-200 text-gray-700'
                  }`}>
                  <div className="flex items-center gap-2">
                    <Clock size={14} />
                    <span className="text-sm">{fmtDateTimeSAST(document.created_at)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Regenerate from Job — for draft billing submissions with no line items */}
            {formData.status === 'draft' && items.length === 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => void handleRegenerateBilling()}
                  disabled={regeneratingBilling}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${regeneratingBilling ? 'opacity-60 cursor-not-allowed' : ''} ${isDarkMode ? 'bg-amber-900/30 text-amber-300 hover:bg-amber-900/50 border border-amber-700/50' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'}`}
                >
                  {regeneratingBilling ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                  {regeneratingBilling ? 'Regenerating...' : 'Regenerate from Job'}
                </button>
                <p className={`mt-1.5 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Re-fetches line items from the job's billable ledger draft.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Payments & Bank Reconciliation - shown for saved documents.
      Payments here are authoritative: matching a bank transaction
      auto-flips the document to paid/partial; unmatching reverses it.
      Hidden for billing_submission as it's not a payable document. */}
        {documentId && doc_type !== 'billing_submission' && (
          <div className="mt-4 border rounded-lg overflow-hidden"
            style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
            <div className={`px-3 py-2 flex items-center justify-between ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-50 text-gray-700'}`}>
              <div className="flex items-center gap-2">
                <CreditCard size={14} />
                <span className="text-xs font-semibold uppercase tracking-wide">
                  Payments & Bank Reconciliation
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const docId = documentId;
                  if (docId) loadDocumentPayments(docId);
                }}
                disabled={loadingPayments}
                className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'}`}
                title="Refresh"
              >
                <RefreshCw size={12} className={loadingPayments ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>

            {paymentsError && (
              <div className="px-3 py-2 text-xs text-red-600 bg-red-50 border-t border-red-200">
                {paymentsError}
              </div>
            )}

            {paymentsTotals && (
              <div className={`px-3 py-2 grid grid-cols-3 gap-2 text-xs ${isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-700'} border-t`} style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
                <div>
                  <div className="opacity-60">Total</div>
                  <div className="font-mono">R {paymentsTotals.grand_total.toFixed(2)}</div>
                </div>
                <div>
                  <div className="opacity-60">Paid</div>
                  <div className="font-mono text-green-600">R {paymentsTotals.amount_paid.toFixed(2)}</div>
                </div>
                <div>
                  <div className="opacity-60">Balance</div>
                  <div className={`font-mono ${paymentsTotals.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    R {paymentsTotals.balance_due.toFixed(2)}
                  </div>
                </div>
              </div>
            )}

            <div className={isDarkMode ? 'bg-gray-900' : 'bg-white'}>
              {documentPayments.length === 0 && !loadingPayments ? (
                <div className={`px-3 py-4 text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  No payments matched yet. When a bank statement is imported,
                  the reconciliation engine will auto-match transactions and
                  update this document's status.
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className={isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Payment</th>
                      <th className="px-3 py-2 text-left font-medium">Bank</th>
                      <th className="px-3 py-2 text-right font-medium">Amount</th>
                      <th className="px-3 py-2 text-right font-medium w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentPayments.map((row) => (
                      <tr
                        key={row.allocation_id}
                        className={`border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}
                      >
                        <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          <div className="font-medium">{row.payment_number || '—'}</div>
                          <div className="opacity-60">
                            {row.payment_date ? fmtDateSAST(row.payment_date) : ''}
                            {row.payment_method ? ` · ${row.payment_method.replace(/_/g, ' ')}` : ''}
                          </div>
                        </td>
                        <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          {row.bank_transaction_id ? (
                            <>
                              <div className="font-medium truncate max-w-[180px]" title={row.bank_description || ''}>
                                {row.bank_description || row.bank_reference || 'Bank transaction'}
                              </div>
                              <div className="opacity-60">
                                {row.bank_name || row.bank_account_name || 'Bank'}
                              </div>
                            </>
                          ) : row.allocation_strategy === 'paid_at_capture' ? (
                            <span className="opacity-60 italic">Paid at capture — awaiting bank match</span>
                          ) : (
                            <span className="opacity-60 italic">Manual payment</span>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          R {Number(row.allocated_amount || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleUnmatchPayment(row)}
                            className="text-red-600 hover:text-red-700 underline"
                            title="Unmatch this payment (reverses status)"
                          >
                            Unmatch
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Create as Recurring Toggle - only for new invoices/expenses */}
        {!document?.id && ['invoice', 'expense'].includes(doc_type) && (
          <div className={`mt-4 p-4 rounded-lg border ${isDarkMode
            ? 'bg-indigo-900/20 border-indigo-700/50'
            : 'bg-indigo-50 border-indigo-200'
            }`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <RefreshCw size={18} className={isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} />
                <span className={`font-medium ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                  Create as Recurring
                </span>
              </div>
              <button
                type="button"
                onClick={() => setCreateAsRecurring(!createAsRecurring)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${createAsRecurring
                  ? 'bg-indigo-600'
                  : isDarkMode ? 'bg-gray-600' : 'bg-gray-200'
                  }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${createAsRecurring ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>

            {createAsRecurring && (
              <div className="space-y-3 mt-3 pt-3 border-t border-indigo-200/50">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Frequency
                    </label>
                    <select
                      value={recurringSettings.frequency}
                      onChange={(e) => setRecurringSettings(prev => ({
                        ...prev,
                        frequency: e.target.value as typeof prev.frequency
                      }))}
                      className={`w-full px-2 py-1.5 text-sm border rounded-lg ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="bi-weekly">Bi-Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={recurringSettings.start_date}
                      onChange={(e) => setRecurringSettings(prev => ({ ...prev, start_date: e.target.value }))}
                      className={`w-full px-2 py-1.5 text-sm border rounded-lg ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {recurringSettings.frequency === 'monthly' && (
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Day of Month
                      </label>
                      <select
                        value={recurringSettings.day_of_month}
                        onChange={(e) => setRecurringSettings(prev => ({ ...prev, day_of_month: Number(e.target.value) }))}
                        className={`w-full px-2 py-1.5 text-sm border rounded-lg ${isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      >
                        {Array.from({ length: 28 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                        <option value={-1}>Last day</option>
                      </select>
                    </div>
                  )}
                  {(recurringSettings.frequency === 'weekly' || recurringSettings.frequency === 'bi-weekly') && (
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Day of Week
                      </label>
                      <select
                        value={recurringSettings.day_of_week}
                        onChange={(e) => setRecurringSettings(prev => ({ ...prev, day_of_week: Number(e.target.value) }))}
                        className={`w-full px-2 py-1.5 text-sm border rounded-lg ${isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      >
                        <option value={0}>Sunday</option>
                        <option value={1}>Monday</option>
                        <option value={2}>Tuesday</option>
                        <option value={3}>Wednesday</option>
                        <option value={4}>Thursday</option>
                        <option value={5}>Friday</option>
                        <option value={6}>Saturday</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      End Date (optional)
                    </label>
                    <input
                      type="date"
                      value={recurringSettings.end_date}
                      onChange={(e) => setRecurringSettings(prev => ({ ...prev, end_date: e.target.value }))}
                      className={`w-full px-2 py-1.5 text-sm border rounded-lg ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={recurringSettings.auto_send}
                      onChange={(e) => setRecurringSettings(prev => ({ ...prev, auto_send: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Auto-send to customer
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={recurringSettings.auto_approve}
                      onChange={(e) => setRecurringSettings(prev => ({ ...prev, auto_approve: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Auto-approve
                    </span>
                  </label>
                </div>

                <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  💡 This will create a recurring schedule. The first {doc_type} will be generated on the start date.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h3 className={`font-semibold border-b pb-2 ${isDarkMode ? 'text-white border-gray-700' : 'text-gray-900 border-gray-200'
          }`}>Shipping & Delivery</h3>

        {config.fields.showShippingMethod && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              Shipping Method
            </label>
            <input
              type="text"
              value={formData.shipping_method || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, shipping_method: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
                }`}
              placeholder="e.g., Courier"
            />
          </div>
        )}

        {config.fields.showShippingCost && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              Shipping Cost
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.shipping_cost || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, shipping_cost: Number(e.target.value) }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
                }`}
              placeholder="0.00"
            />
          </div>
        )}

        {config.fields.showTrackingNumber && (
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
              Tracking Number
            </label>
            <input
              type="text"
              value={formData.tracking_number || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, tracking_number: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
                }`}
              placeholder="Tracking number"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default DetailsTab;
